import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { LeadType } from "views/leads-id/types";
import { usePropertyPhoneDefault } from "../../phone-numbers/lib/hooks";
import { usePropertyDefaultEmail } from "../../properties-id/lib/propertyDefaultEmail";
import { useMessages } from "../api/hooks";
import type { CrmContact } from "../api/crm";
import type { Message } from "../types";
import { digitsOnly } from "../utils/contactMethods";
import { formatTimestamp } from "../utils/dateUtils";

export type SenderRecommendationKind =
  | "last_used"
  | "property_default"
  | "property_assigned";

export interface SenderRecommendation {
  value: string;
  kind: SenderRecommendationKind;
  reason: string;
  propertyName?: string;
}

interface SingleProperty {
  id: number;
  name?: string;
}

export const useContactSingleProperty = (
  contact: CrmContact | undefined
): SingleProperty | undefined =>
  useMemo(() => {
    const properties = new Map<number, string | undefined>();
    (contact?.leads ?? []).forEach((lead) => {
      if (lead.lead_type === LeadType.Owner) return;
      if (lead.property_id != null)
        properties.set(
          lead.property_id,
          properties.get(lead.property_id) ?? lead.property?.name
        );
    });
    (contact?.applicants ?? []).forEach((applicant) => {
      const property = applicant.application?.property;
      if (property?.id != null)
        properties.set(
          property.id,
          properties.get(property.id) ?? property.name
        );
    });
    if (properties.size !== 1) return undefined;
    const [[id, name]] = [...properties];
    return { id, name };
  }, [contact]);

interface OurSideMessage {
  message: Message;
  addresses: string[];
}

interface MessageSource {
  messages: unknown[];
  ourSide: (message: Message) => string[];
}

const sourcesOf = (
  pages: { messages: unknown[] }[] | undefined,
  ourSide: (message: Message) => string[]
): MessageSource[] =>
  (pages ?? []).map((page) => ({ messages: page.messages, ourSide }));

const newestMessage = (sources: MessageSource[]): OurSideMessage | undefined =>
  sources
    .flatMap(({ messages, ourSide }) =>
      (messages as Message[]).map((message) => ({
        message,
        addresses: ourSide(message).filter(Boolean)
      }))
    )
    .filter((entry) => entry.addresses.length > 0)
    .reduce<OurSideMessage | undefined>(
      (newest, entry) =>
        !newest ||
        Date.parse(entry.message.created_at) >
          Date.parse(newest.message.created_at)
          ? entry
          : newest,
      undefined
    );

const smsOurSide = (message: Message) => {
  const details = message.sms_details;
  if (!details) return [];
  return [
    message.direction === "outbound" ? details.from_number : details.to_number
  ];
};

const voiceOurSide = (message: Message) => {
  const details = message.voice_details;
  if (!details) return [];
  const ours =
    message.direction === "outbound" ? details.from_number : details.to_number;
  return [ours ?? ""];
};

const emailOurSide = (message: Message) => {
  const details = message.email_details;
  if (!details) return [];
  if (message.direction === "outbound") return [details.from_address];
  return [...details.to_addresses, ...(details.cc_addresses ?? [])];
};

const lastUsedWhen = (createdAt: string) => {
  const days = DateTime.now()
    .startOf("day")
    .diff(DateTime.fromISO(createdAt).startOf("day"), "days").days;
  if (days < 1) return "today";
  if (days < 2) return "yesterday";
  return `on ${formatTimestamp(createdAt)}`;
};

const lastUsedReason = (contact: CrmContact | undefined, createdAt: string) => {
  const name = contact?.first_name?.trim();
  return `Last used with ${name || "this contact"} ${lastUsedWhen(createdAt)}`;
};

const defaultReason = (propertyName?: string) =>
  propertyName ? `Default for ${propertyName}` : "Property default";

const assignedReason = (propertyName?: string) =>
  propertyName ? `Assigned to ${propertyName}` : "Assigned to this property";

interface SmsNumber {
  communication_service_id: string;
  phone_number?: string | null;
  assign_all_properties?: boolean;
  assigned_properties?: { id: number; name: string }[];
}

const assignedNumberFor = (numbers: SmsNumber[], propertyId: number) => {
  const assigned = numbers.filter(
    (number) =>
      !number.assign_all_properties &&
      Boolean(
        number.assigned_properties?.some(
          (property) => property.id === propertyId
        )
      )
  );
  return assigned.length === 1 ? assigned[0] : undefined;
};

const pickSmsRecommendation = ({
  last,
  defaultPhone,
  property,
  numbers,
  idByDigits,
  contact
}: {
  last: OurSideMessage | undefined;
  defaultPhone: string | undefined;
  property: SingleProperty | undefined;
  numbers: SmsNumber[];
  idByDigits: Map<string, string>;
  contact: CrmContact | undefined;
}): SenderRecommendation | null => {
  const lastId = last && idByDigits.get(digitsOnly(last.addresses[0]));
  if (last && lastId)
    return {
      value: lastId,
      kind: "last_used",
      reason: lastUsedReason(contact, last.message.created_at)
    };
  const defaultId = defaultPhone && idByDigits.get(digitsOnly(defaultPhone));
  if (defaultId)
    return {
      value: defaultId,
      kind: "property_default",
      reason: defaultReason(property?.name),
      propertyName: property?.name
    };
  if (!property) return null;
  const assigned = assignedNumberFor(numbers, property.id);
  if (!assigned) return null;
  const propertyName =
    property.name ??
    assigned.assigned_properties?.find((item) => item.id === property.id)?.name;
  return {
    value: assigned.communication_service_id,
    kind: "property_assigned",
    reason: assignedReason(propertyName),
    propertyName
  };
};

interface RecommendedSenderParams {
  enabled: boolean;
  conversationId?: string;
  contact: CrmContact | undefined;
}

export const useRecommendedSmsSender = ({
  enabled,
  conversationId,
  contact,
  numbers
}: RecommendedSenderParams & {
  numbers: SmsNumber[];
}): SenderRecommendation | null => {
  const property = useContactSingleProperty(contact);
  const threadId = enabled ? (conversationId ?? "") : "";
  const { data: smsMessages } = useMessages(threadId, {
    channel: "sms",
    limit: 1
  });
  const { data: voiceMessages } = useMessages(threadId, {
    channel: "voice",
    limit: 1
  });
  const { data: propertyDefault } = usePropertyPhoneDefault(
    property?.id ?? 0,
    enabled && property !== undefined
  );

  const idByDigits = useMemo(
    () =>
      new Map(
        numbers.map((number) => [
          digitsOnly(number.phone_number ?? ""),
          number.communication_service_id
        ])
      ),
    [numbers]
  );

  return useMemo(() => {
    if (!enabled) return null;
    const last = newestMessage([
      ...sourcesOf(smsMessages?.pages, smsOurSide),
      ...sourcesOf(voiceMessages?.pages, voiceOurSide)
    ]);
    return pickSmsRecommendation({
      last,
      defaultPhone: propertyDefault?.phone_number,
      property,
      numbers,
      idByDigits,
      contact
    });
  }, [
    enabled,
    smsMessages,
    voiceMessages,
    propertyDefault,
    property,
    numbers,
    idByDigits,
    contact
  ]);
};

export const useRecommendedEmailSender = ({
  enabled,
  conversationId,
  contact,
  mailboxIdByEmail
}: RecommendedSenderParams & {
  mailboxIdByEmail: Map<string, string>;
}): SenderRecommendation | null => {
  const property = useContactSingleProperty(contact);
  const { data: messages } = useMessages(
    enabled ? (conversationId ?? "") : "",
    { channel: "email", limit: 1 }
  );
  const { data: propertyDefault } = usePropertyDefaultEmail(
    property?.id ?? 0,
    enabled && property !== undefined
  );

  return useMemo(() => {
    if (!enabled) return null;
    const last = newestMessage(sourcesOf(messages?.pages, emailOurSide));
    const lastId = last?.addresses
      .map((address) => mailboxIdByEmail.get(address.toLowerCase()))
      .find(Boolean);
    if (last && lastId)
      return {
        value: lastId,
        kind: "last_used",
        reason: lastUsedReason(contact, last.message.created_at)
      };
    const defaultId = propertyDefault?.default_mailbox_id;
    if (defaultId && [...mailboxIdByEmail.values()].includes(defaultId))
      return {
        value: defaultId,
        kind: "property_default",
        reason: defaultReason(property?.name),
        propertyName: property?.name
      };
    return null;
  }, [enabled, messages, mailboxIdByEmail, contact, propertyDefault, property]);
};

export const useSenderMismatch = ({
  selected,
  effective,
  recommendation
}: {
  selected: string | null;
  effective: string;
  recommendation: SenderRecommendation | null;
}) => {
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const mismatched = Boolean(
    recommendation &&
    selected !== null &&
    effective &&
    effective !== recommendation.value
  );
  return {
    mismatched,
    dialogOpen: mismatched && dismissedFor !== effective,
    dismiss: () => setDismissedFor(effective)
  };
};
